import { Component, HostListener, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AsyncSubject, filter, lastValueFrom } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';

import { MarketPlaceQuoteRequestService } from 'src/app/services/market-place-quote-request-service/market-place-quote-request.service';

import {
  CarOption,
  CarOptionObj,
} from 'src/app/models/filter-car-type-option.model';
import { FilterComponentModel } from 'src/app/models/filter-component.model';
import { StccsOption } from 'src/app/models/stcc-option.model';
import {
  AggDetailsRes,
  RailCar,
  RailCarEndpointRes,
} from 'src/app/models/railcar.model';
import { OptionModel } from 'src/app/models/railcar-display-options.model';
import {
  CAR_TYPE,
  CarTypeDropdownLabel,
  CarTypeLabel,
  CountryCode,
  STCCCarType,
} from './search-cars.helper.enums';
import {
  carDetailAttributeList,
  defaultFilterState,
  filterDetailObj,
  filterSelectionState,
  multiOptionsMessage,
  noCarsFoundMessage,
  OTHER,
} from './search-cars.helper.consts';
import { ActiveFilterState } from 'src/app/models/active-filters.model';
import { draftQuoteRequest } from 'src/app/services/market-place-quote-request-service/draft-quote-request.service';
import { MixpanelService } from '../../services/mixpanel-service/mixpanel.service';

@Component({
  selector: 'app-search-cars',
  templateUrl: './search-cars.component.html',
  styleUrls: ['./search-cars.component.scss'],
})
export class SearchCarsComponent implements OnInit {
  loading = false;
  matchCt = 0;

  selectedNum = 0;

  filterState: any = {
    CarType: {
      displayAs: 'CarType',
      values: undefined,
    },
  };

  banner: {
    enabled: boolean;
    message: string;
    header?: string;
    color: string;
  } = {
    enabled: false,
    message: '',
    color: '',
  };

  displayedCars: RailCar[] = [];

  cardAttrList: OptionModel[] = [];
  detailAttrList: OptionModel[] = [];

  filters: FilterComponentModel[] = [];

  railCarOptions: CarOptionObj = {};

  carFilterObj: FilterComponentModel = {
    field: 'CarType',
    title: 'CarType',
    open: false,
    values: [],
    displayAs: 'CarType',
  };

  private stccOptionsSource$ = new AsyncSubject<StccsOption[]>();
  stccOptions$ = this.stccOptionsSource$.asObservable();
  activeStcc: StccsOption | undefined;
  stccCarList: string[] = [];

  productName = '';
  hildfilter = false;

  constructor(
    public quoteRequestService: MarketPlaceQuoteRequestService,
    public dialogService: DialogService,
    private router: Router,
    private draftService: draftQuoteRequest,
    private mixpanelService: MixpanelService
  ) {
    this.router.events
      .pipe(filter((rs): rs is NavigationEnd => rs instanceof NavigationEnd))
      .subscribe((event) => {
        if (event.id === 1 && event.url === event.urlAfterRedirects) {
          const data = sessionStorage.getItem(filterSelectionState);
          if (!data) {
            this.router.navigate(['/search-inventory'], {
              state: { refresh: true },
            });
          }
          window.scrollTo(0, 0);
        }
      });
  }

  @HostListener('window:beforeunload', ['$event']) unloadHandler(event: Event) {
    const CarType = this.filterState.CarType.obj
      ? this.filterState.CarType.obj.field
      : undefined;
    const ProductName =
      this.activeStcc?.stcc === 'Other' ? this.productName : undefined;

    const data = { StccObj: this.activeStcc, CarType, ProductName };
    sessionStorage.setItem(filterSelectionState, JSON.stringify(data));
  }

  ngOnInit(): void {
    this.getSTCCList();
    this.setDefaultFilterVals();
    sessionStorage.removeItem(filterSelectionState);
    this.quoteRequestService.setSelectedCarList([], []);
  }

  applyFiltertCarList({
    railCars,
    entries,
    modelIdList,
  }: {
    railCars: RailCar[];
    entries: [string, FilterComponentModel][];
    modelIdList: number[];
  }): RailCar[] {
    const allArray = ['All', 'No Preference'];
    enum EntryMap {
      'Yes' = 'Coils',
      'No' = 'No Coils',
    }
    const reducedEntries = entries.reduce(
      (res, entry) => {
        const { displayAs, values } = entry[1];
        if (displayAs !== 'checkbox' && displayAs !== 'multiselect') {
          res.push(entry);
          return res;
        }
        if (values.some((x) => allArray.includes(x.name))) return res;

        entry[1].values = values.map((x) => {
          const name = EntryMap[x.name as keyof typeof EntryMap];
          return name ? { ...x, name } : x;
        });
        res.push(entry);
        return res;
      },
      [] as [string, FilterComponentModel][]
    );

    return railCars.filter((car) =>
      reducedEntries.every((x) => {
        const carAtt = `${x[1].carAttributeName}`;
        switch (x[1].displayAs) {
          case 'CarType':
            return modelIdList.includes(car.model_id);
          case 'range':
            return (
              (!x[1].MIN || car[carAtt] >= x[1].MIN) &&
              (!x[1].MAX || car[carAtt] <= x[1].MAX)
            );
          case 'location': {
            const field = x[0] === 'state' ? 'code' : 'name';
            return x[1].values.some((y) => y[field] === `${car[carAtt]}`);
          }
          default:
            return x[1].values.some((y) => y.name === `${car[carAtt]}`);
        }
      })
    );
  }

  async filterList(): Promise<RailCarEndpointRes> {
    const carType = this.filterState.CarType.obj.field;
    const railcarsOfType = await lastValueFrom(
      this.quoteRequestService.carList$
    ).then((res) => Object.entries(res).find((x) => x[0] === carType));
    const { railCars, carDetails } =
      railcarsOfType !== undefined
        ? railcarsOfType[1]
        : await this.quoteRequestService.getCarList();
    const modelIdList: number[] = this.filterState.CarType.obj.model_ids || [];
    const entries = Object.entries(
      JSON.parse(JSON.stringify(this.filterState))
    ) as [string, FilterComponentModel][];
    return {
      railCars: this.applyFiltertCarList({ railCars, entries, modelIdList }),
      carDetails,
    };
  }

  async getSTCCList(): Promise<void> {
    let options = await lastValueFrom(this.quoteRequestService.stccList$).then(
      (x) => [OTHER, ...x]
    );
    if (this.filterState.CarType.obj) {
      const stccFormat =
        CarTypeLabel[
          this.filterState.CarType.obj.field as keyof typeof CarTypeLabel
        ];
      options = options.filter(
        (x) => x.car_types?.length === 0 || x.car_types?.includes(stccFormat)
      );
    } else {
      options = options.filter((x) => x.car_types !== null);
    }
    this.stccOptionsSource$.next(options);
    this.stccOptionsSource$.complete();
  }

  async setDefaultFilterVals(): Promise<void> {
    const selectionConfig = sessionStorage.getItem('selections');

    if (selectionConfig) {
      const { ProductName, StccObj, CarType } = JSON.parse(selectionConfig);
      this.activeStcc = StccObj;
      this.quoteRequestService.setDefaultStcc(StccObj);
      this.productName = ProductName;
      this.quoteRequestService.setProductName(ProductName);
      if (CarType) this.quoteRequestService.selectCarType(CarType);
      sessionStorage.removeItem(filterSelectionState);
    }
    const railCarOptions = await lastValueFrom(
      this.quoteRequestService.carTypeOptions$
    );
    this.railCarOptions = railCarOptions;
    this.updateCarFilterObj(railCarOptions);

    const defaultStccObj = this.quoteRequestService.getDefaultStcc();
    if (defaultStccObj) this.setDefaultStcc(defaultStccObj);

    const defaultCarType = this.quoteRequestService.getSelectedCarType();

    const defaultCar = Object.keys(railCarOptions).find(
      (x) => x === defaultCarType
    );
    if (defaultCar) this.setDefaultCarType(defaultCar);
  }

  updateCarFilterObj(railCarOptions: CarOptionObj): void {
    this.carFilterObj = {
      field: 'CarType',
      title: 'CarType',
      open: false,
      values: Object.entries(railCarOptions).map((x) => ({
        ...x[1],
        field: x[0],
        inactive:
          this.stccCarList.length > 0
            ? !this.stccCarList.includes(x[0])
            : false,
        dropdownDisplay:
          CarTypeDropdownLabel[x[0] as keyof typeof CarTypeDropdownLabel],
      })),
      displayAs: 'CarType',
    };
  }

  setDefaultStcc(defaultStccObj: StccsOption): void {
    this.activeStcc = defaultStccObj;
    this.productName = this.quoteRequestService.getProductName();
    this.stccUpdated(defaultStccObj);
  }

  selectedStcc(value: {
    filter: FilterComponentModel;
    selected: StccsOption;
  }): void {
    this.activeStcc = value.selected;
    this.stccUpdated(this.activeStcc);
    this.quoteRequestService.setDefaultStcc(this.activeStcc);
    this.mixpanelService.track({
      eventName: 'Product',
      eventDisplayName: this.activeStcc ? this.activeStcc.description_stcc : '',
      kpi: 'User Engagement',
      trigger: 'On dropdown change',
      eventDefinition: 'Product filter',
      propertyType: 'Event Property',
      propertyDefinition: 'Selection',
    });
  }

  selectedProductName(value: {
    filter: FilterComponentModel;
    selected: string;
  }): void {
    this.productName = value.selected;
    this.quoteRequestService.setProductName(this.productName);

    // Track the event with Mixpanel
    this.mixpanelService.track({
      eventName: 'Product Name',
      eventDisplayName: this.productName,
      kpi: 'User Engagement',
      trigger: 'On dropdown change',
      eventDefinition: 'Product name',
      propertyType: 'Event Property',
      propertyDefinition: 'Selection',
    });
  }

  stccUpdated(stccObj: StccsOption): void {
    this.stccCarList = !stccObj.car_types
      ? []
      : stccObj.car_types.map((x) => STCCCarType[x]);
    this.updateCarFilterObj(this.railCarOptions);
    this.saveFilterState();

    if (this.stccCarList.length === 1) {
      const defaultCar = Object.keys(this.railCarOptions).find(
        (x) => x === this.stccCarList[0]
      );

      if (defaultCar) this.setDefaultCarType(defaultCar);
      return;
    }

    const carTypeObj = this.filterState.CarType.obj;
    if (carTypeObj !== undefined) {
      this.updateCarTypeRes(carTypeObj);
      return;
    }

    const defaultCarType = this.quoteRequestService.getSelectedCarType();
    if (!defaultCarType)
      this.enableBanner(
        !stccObj.car_types ? noCarsFoundMessage : multiOptionsMessage
      );
  }

  setCarTypeFilterState(obj: any): void {
    const { title, dropdownDisplay } = obj;

    this.filterState = {
      CarType: {
        displayAs: 'CarType',
        obj,
        values: [title],
        carAttributeName: 'car_type',
        dropdownDisplay,
      },
    };
  }

  setDefaultCarType(field: string): void {
    const selected = this.carFilterObj.values.find((x) => x.field === field);
    this.setCarTypeFilterState(selected);
    this.updateCarTypeRes(selected);
    this.saveFilterState();
  }

  async updateCarTypeRes(selected: CarOption): Promise<void> {
    this.selectCarType(selected);
    const railcarsOfType = await lastValueFrom(
      this.quoteRequestService.carList$
    ).then((res) => Object.entries(res).find((x) => x[0] === selected.field));
    if (railcarsOfType === undefined) {
      return;
    }

    this.updateDisplayVars(railcarsOfType[1]);
  }

  selectCarType(value: CarOption): void {
    const allArray = ['All', 'No Preference'];
    const { filters, cardAttrList, detailAttrList } = Object.entries(
      value.options
    )
      .reduce((res, option) => {
        if (Array.isArray(option[1])) {
          const values = option[1].map((x) =>
            x.states === null ? { ...x, states: [] } : x
          );
          res.push({
            ...filterDetailObj[option[0]].filterElements,
            title: 'Location',
            values,
          });
          return res;
        }
        if (option[1].values.length === 0) return res;
        const filterEl = filterDetailObj[option[0]].filterElements;
        if (option[1].uom_values) {
          const subtitle =
            option[1].uom_values.length > 0 ? option[1].uom_values[0].name : '';
          const values = option[1].values
            .map((x) => +x.name)
            .sort((a, b) => a - b);
          res.push({ ...option[1], ...filterEl, values, subtitle });
          return res;
        }
        res.push({ ...option[1], ...filterEl });
        const allVal = option[1].values.find((x: { name: string }) =>
          allArray.includes(x.name)
        );
        if (allVal === undefined) return res;
        this.filterState[option[0]] = {
          carAttributeName: filterEl.carAttributeName,
          displayAs: filterEl.displayAs,
          values: [allVal],
        };
        return res;
      }, [] as OptionModel[])
      .sort((a, b) => a.rank - b.rank)
      .reduce(
        (res, attr) => {
          const { filters, cardAttrList, detailAttrList } = res;
          const filterObj = { ...attr, open: !!attr.alwaysOpen };
          filters.push(filterObj);

          const { cardElements, detailElements } = filterDetailObj[attr.field];
          if (cardElements) {
            cardAttrList.push({ ...attr, ...cardElements });
            detailAttrList.push({ ...attr, ...cardElements });
          }
          if (detailElements) {
            detailAttrList.push({
              ...attr,
              ...detailElements,
              labelTail: `(${filterObj.subtitle})`,
            });
          }
          return { filters, cardAttrList, detailAttrList };
        },
        { filters: [], cardAttrList: [], detailAttrList: [] } as {
          filters: FilterComponentModel[];
          cardAttrList: OptionModel[];
          detailAttrList: OptionModel[];
        }
      );
    this.filters = filters;
    this.cardAttrList = cardAttrList;
    this.detailAttrList = detailAttrList;
    this.draftService.setSelectedCarListDraft(cardAttrList);
    if (!value.field) {
      this.saveFilterState();
      return;
    }
    this.quoteRequestService.selectCarType(value.field);
    this.filterStccList(value.field);
  }

  async filterStccList(field: CAR_TYPE): Promise<void> {
    const stccFormat = CarTypeLabel[field as keyof typeof CarTypeLabel];
    const options = await lastValueFrom(
      this.quoteRequestService.stccList$
    ).then((x) => [OTHER, ...x]);
    const stccList = options.filter(
      (x) => x.car_types?.length === 0 || x.car_types?.includes(stccFormat)
    );
    this.stccOptionsSource$ = new AsyncSubject<StccsOption[]>();
    this.stccOptions$ = this.stccOptionsSource$.asObservable();
    this.stccOptionsSource$.next(stccList);
    this.stccOptionsSource$.complete();
  }

  selectionReciever(value: any): void {
    const fieldName = value.filter.name;
    const filterTitle = value?.filter?.title; // Extracting title from the filter

    if (
      fieldName === 'CarType' &&
      this.filterState.CarType.obj.title === value.title
    ) {
      return;
    }
    const { displayAs, field, carAttributeName } = value.filter;
    switch (displayAs) {
      case 'CarType': {
        this.setCarTypeFilterState(value.selected);
        break;
      }
      case 'range': {
        const capacityRange = this.filterState[field] || {
          displayAs: 'range',
          carAttributeName,
        };
        capacityRange[value.range] = value.selected;
        this.filterState[field] = capacityRange;
        break;
      }
      default: {
        value.selected.length === 0
          ? delete this.filterState[field]
          : (this.filterState[field] = {
              displayAs,
              values: value.selected,
              carAttributeName,
            });
      }
    }
    if (displayAs === 'CarType')
      this.selectCarType(this.filterState.CarType.obj);
    const fieldValues = this.filterState[field]?.values || [];
    this.updateConditionalFilters({ field, fieldValues });
    this.makeCarListApiCall();
    this.mixpanelService.track({
      eventName: 'Filter Selection',
      eventDisplayName: `Selected ${filterTitle}`,
      propertyType: fieldName,
      eventDefinition: `User selected ${filterTitle} with values ${value.selected.map((item: { name: any }) => item.name).join(', ')}`,
      propertyDefinition: value.selected
        .map((item: { name: any }) => item.name)
        .join(', '),
    });
  }

  async makeCarListApiCall(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    const displayCars = await this.filterList().finally(
      () => (this.loading = false)
    );
    this.updateDisplayVars(displayCars);
    this.saveFilterState();
  }

  updateConditionalFilters({
    field,
    fieldValues,
  }: {
    field: string;
    fieldValues: { id: number; name: string }[];
  }): void {
    this.filters.forEach((filter, index) => {
      if (filter.displayWhen?.field !== field) return;
      const { displayWhen } = filter;
      const isVisible = fieldValues.some((filterVal) =>
        displayWhen.value.includes(filterVal.name)
      );
      this.filters[index].displayWhen = { ...displayWhen, isVisible };
      if (!isVisible && !!this.filterState[filter.field]) {
        delete this.filterState[filter.field];
      }
    });
  }

  updateDisplayVars({ railCars, carDetails }: RailCarEndpointRes): void {
    const aggregatedCars = this.aggrigateCarsByModelID({
      railCars,
      carDetails,
    });
    this.displayedCars = aggregatedCars;
    this.matchCt = aggregatedCars.length;
    aggregatedCars.length === 0
      ? this.enableBanner(noCarsFoundMessage)
      : this.disableBanner();
  }

  getDisplayRates(
    car1: RailCar,
    arr: number[],
    label: 'rate' | 'rate2'
  ): number {
    switch (arr.length) {
      case 0:
        return car1[label];
      case 1:
        return arr[0];
      default:
        return label === 'rate' ? Math.min(...arr) : Math.max(...arr);
    }
  }

  getDisplayedAggrigatedDetails({
    railCars,
    carDetails,
  }: RailCarEndpointRes): AggDetailsRes {
    const carDetailKeys = Object.keys(carDetails) as (keyof AggDetailsRes)[];
    const displayed: AggDetailsRes = {
      aggregated_age_range: [],
      aggregated_compliance_due: [],
    };

    return railCars.reduce(
      (carRes, car) => {
        return carDetailKeys.reduce((res, key) => {
          const detailIndex = res.carDetails[key].findIndex((detail) =>
            carDetailAttributeList.every((atr) => car[atr] === detail[atr])
          );
          if (detailIndex === -1) return res;
          res.displayed[key].push(res.carDetails[key][detailIndex] as any);
          res.carDetails[key].splice(detailIndex, 1);
          return res;
        }, carRes);
      },
      { displayed, carDetails }
    ).displayed;
  }

  getDisplayLocation(cars: RailCar[], car1: RailCar): string {
    const diffLocal = cars.some(
      (x) =>
        x.country !== car1.country ||
        x.country === 'TBD' ||
        x.state !== car1.state
    );
    if (diffLocal) return 'MULTI';
    const country_code = car1.country_code
      ? car1.country_code
      : CountryCode[car1.country as keyof typeof CountryCode] || car1.country;
    return `${car1.state}, ${country_code}`;
  }

  getStccCarList(railCars: RailCar[]): RailCar[] {
    if (!this.activeStcc) return railCars;
    const { stcc, model_ids } = this.activeStcc as StccsOption;
    if ([undefined, 'Other'].includes(stcc) || !model_ids) return railCars;

    return railCars.filter((car: RailCar) =>
      (model_ids as number[]).includes(car.model_id)
    );
  }

  aggrigateCarsByModelID({
    railCars,
    carDetails,
  }: RailCarEndpointRes): RailCar[] {
    const stccCarList = this.getStccCarList(railCars);
    const carListByModel = stccCarList.reduce(
      (res, railCar) => {
        const { model_id, status = 'undefined' } = railCar;
        if (!res[model_id]) res[model_id] = {};
        if (!res[model_id][status]) res[model_id][status] = [];
        res[model_id][status].push(railCar);
        return res;
      },
      {} as { [x: string]: { [status: string]: RailCar[] } }
    );

    const empty = { aggregated_age_range: [], aggregated_compliance_due: [] };
    const details = (Object.keys(carDetails) as (keyof AggDetailsRes)[]).reduce(
      (keyRes, key) => {
        return (carDetails[key] as { [x: string]: any }[]).reduce(
          (res, detail) => {
            const model_id = detail['model_id'];
            if (!res[model_id]) res[model_id] = empty;
            res[model_id][key].push(detail);
            return res;
          },
          keyRes
        );
      },
      {} as { [x: number]: AggDetailsRes }
    );

    const detailList = this.detailAttrList.map((x) => x.field);

    return Object.values(carListByModel)
      .flatMap((statusGroups) =>
        Object.values(statusGroups).map<RailCar>((cars) => {
          const count = cars.reduce((total, car) => total + car.count, 0);
          const { rateArr, rate2Arr } = cars.reduce(
            (res, car) => {
              if (car.rate !== null) res.rateArr.push(car.rate);
              if (car.rate2 !== null) res.rate2Arr.push(car.rate2);
              return res;
            },
            { rateArr: [], rate2Arr: [] } as {
              rateArr: number[];
              rate2Arr: number[];
            }
          );
          const car1 = cars[0];
          const display_location = this.getDisplayLocation(cars, car1);
          const rate = this.getDisplayRates(car1, rateArr, 'rate');
          const rate2 = this.getDisplayRates(car1, rate2Arr, 'rate2');
          const aggregated = this.getDisplayedAggrigatedDetails({
            railCars: cars,
            carDetails: details[car1.model_id],
          });
          const displayValues = detailList.reduce(
            (res, atr) => ({
              ...res,
              [atr]: cars.some((x) => x[atr] !== car1[atr])
                ? 'Multi'
                : car1[atr],
            }),
            {} as { [x: string]: any }
          );
          return {
            ...car1,
            ...displayValues,
            count,
            display_location,
            rate,
            rate2,
            aggregatedCars: cars,
            aggregated,
          };
        })
      )
      .sort((a, b) => `${a.model_desc}`.localeCompare(b.model_desc));
  }
  getFilterStateCapacity(): {
    capacity: [number, number];
    capacity_uom: string;
  } {
    let { capacity, capacity_uom } = defaultFilterState;
    const capacityObj = this.filters.find((x) => x.field === 'capacity');
    if (!capacityObj) return { capacity, capacity_uom };

    const { values, subtitle } = capacityObj;
    capacity = [values[0], values[values.length - 1]];
    return { capacity, capacity_uom: subtitle || '' };
  }

  saveFilterState(): void {
    const capacityValues = this.getFilterStateCapacity();
    const activeFilterState: ActiveFilterState = {
      ...defaultFilterState,
      ...capacityValues,
      description_stcc: this.activeStcc?.description_stcc || '',
    };
    Object.values(this.filterState).forEach((filterEntry) => {
      const {
        carAttributeName: name,
        MIN,
        MAX,
        displayAs,
        values,
      } = filterEntry as FilterComponentModel;
      if (name === undefined) return;

      switch (displayAs) {
        case 'CarType': {
          activeFilterState[name] = values ? values[0] : '';
          break;
        }
        case 'range': {
          MIN ? (activeFilterState[name][0] = MIN) : null;
          MAX ? (activeFilterState[name][1] = MAX) : null;
          break;
        }
        default: {
          activeFilterState[name] = values.map((x) => x.name);
        }
      }
    });
    this.quoteRequestService.setActiveFilter(activeFilterState);
  }

  toggleFilterOpen(filterIndex: number): void {
    if (this.filters[filterIndex].alwaysOpen) {
      return;
    }
    this.filters[filterIndex].open = !this.filters[filterIndex].open;
  }

  enableBanner(input: {
    message: string;
    header?: string;
    color: string;
  }): void {
    this.banner = { enabled: true, ...input };
  }

  disableBanner(): void {
    this.banner.enabled = false;
  }
  displayFilter(): void {
    this.hildfilter = true;
  }
  filterAction() {
    this.hildfilter = false;
  }
  toggleFilter() {
    this.hildfilter = false;
  }
}
